import type { ReceiptDocument } from '@sp/core';
import { hasKey, tr } from '../i18n';
/**
 * Response shapes the UI consumes. These mirror the API contract exactly; anything the UI
 * needs that the API does not return is a gap to fix on the server, not something to invent
 * here.
 */

import type { BillDiscount, LineDiscount } from '@sp/core';

export interface Principal {
  userId: string;
  username: string;
  fullName: string;
  role: string;
  permissions: string[];
  storeId: string | null;
  terminalId: string | null;
}

export interface LoginResult {
  tokens: { accessToken: string; refreshToken: string; expiresAt: string };
  storeId: string;
  principal: Principal;
}

/** What kind of business this is and which modules it runs (packages/core/src/modules.ts). */
export interface BusinessProfileInfo {
  businessType: string;
  plan?: string;
  enabledModules: string[];
}

/** The owner's alert thresholds (Admin -> Settings), served with the session. */
export interface InsightSettings {
  /** A supplier cost rise of at least this many basis points is flagged (300 = 3%). */
  costAlertBps: number;
}

export interface MeResult {
  user: Principal;
  store: { id: string; name: string; code: string; gstin: string | null } | null;
  /** Absent from an API older than the businessProfile feature: treat every module as on. */
  businessProfile?: BusinessProfileInfo;
  insightSettings?: InsightSettings;
}

/** `gstBps` is basis points: 1800 = 18%. Money is paise, quantity is milli-units. */
export interface PosProduct {
  id: string;
  sku: string;
  barcode: string | null;
  name: string;
  localName: string | null;
  unit: string;
  categoryId: string | null;
  categoryName: string | null;
  brandId: string | null;
  brandName: string | null;
  taxRateId: string;
  taxRateName: string;
  gstBps: number;
  cessBps: number;
  hsnCode: string | null;
  sellingPricePaise: number;
  purchasePricePaise: number;
  mrpPaise: number;
  pricingInclusive: boolean;
  isBatchTracked: boolean;
  isExpiryTracked: boolean;
  allowDecimalQty: boolean;
  stockMilli: number;
  /** Replenishment levels, used by the inventory screen to flag "below minimum". */
  minimumStockMilli: number;
  reorderLevelMilli: number;
  reorderQuantityMilli: number;
  nextExpiryDate: string | null;
  isActive: boolean;
  /** Present on list/search responses only. */
  barcodes?: string[];
}

export interface Paged<T> {
  items: T[];
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
}

/** Mirrors the `cash_shift` row served by `/api/pos/shifts/*`. */
export interface Shift {
  id: string;
  storeId: string;
  terminalId: string;
  userId: string;
  cashierName: string | null;
  state: 'OPEN' | 'CLOSED';
  openingCashPaise: number;
  expectedCashPaise: number;
  actualCashPaise: number | null;
  differencePaise: number | null;
  differenceNote: string | null;
  openedAt: string;
  closedAt: string | null;
}

/**
 * What a held bill stores: enough to rebuild the cart exactly as the cashier left it.
 * The discounts reuse the core types, so a held bill cannot hold a shape the sale engine
 * would refuse when it is finally resumed and billed.
 */
export interface HoldPayload {
  lines: {
    productId: string;
    qtyMilli: number;
    ratePaise: number;
    batchId: string | null;
    discount: LineDiscount;
    /** Pinned at hold time so a GST/price change cannot silently reprice the resume. */
    taxRateId?: string | null;
    gstBps?: number | null;
    cessBps?: number | null;
    pricingInclusive?: boolean | null;
    hsnCode?: string | null;
    mrpPaise?: number | null;
    /** An open item: the name typed at the till and its Item Tax rate. */
    openItem?: { name: string; taxRateId: string | null } | null;
  }[];
  billDiscount: BillDiscount;
  /** "No Tax" as it was when the bill was held; absent on older holds, which resume taxed. */
  taxExempt?: 'discount' | 'exempt' | null;
  taxExemptNote?: string | null;
  /** The attached customer survives the hold; absent on older holds. */
  customerId?: string | null;
  customer?: { id: string; name: string; phone: string; stateCode?: string | null } | null;
  note?: string | null;
}

export interface HeldSale {
  id: string;
  holdNumber: string;
  terminalId: string;
  label: string | null;
  totalPaise: number;
  heldAt: string;
  status: string;
  customerId?: string | null;
  payload: HoldPayload;
}

export interface PaymentLeg {
  method: PaymentMethod;
  amountPaise: number;
  reference: string | null;
}

export type PaymentMethod = 'CASH' | 'UPI' | 'CARD' | 'OTHER' | 'NETBANKING' | 'CHEQUE' | 'WALLET' | 'CREDIT';

/** A payment method's name in the current language (an unknown method shows its code). */
export function methodLabel(method: string): string {
  const key = `method.${method}`;
  return hasKey(key) ? tr(key) : method;
}

/** Methods the till can take. The server accepts exactly CASH, UPI, CARD and OTHER. */
export const TENDER_KEYS: PaymentMethod[] = ['CASH', 'UPI', 'CARD', 'OTHER'];

export interface CompleteSaleResult {
  saleId: string;
  invoiceNumber: string;
  grandTotalPaise: number;
  roundOffPaise: number;
  changePaise: number;
  payments: PaymentLeg[];
  warnings: string[];
  receipt: string | null;
  /** The structured receipt; absent on an older API. Tamil and bilingual receipts are drawn from it. */
  receiptDocument?: ReceiptDocument | null;
}


/** Mirrors the customer row served by /api/customers and /api/customers/lookup. */
export interface Customer {
  id: string;
  stateCode?: string | null;
  isActive?: boolean;
  name: string;
  phone: string;
  email: string | null;
  gstin: string | null;
  isB2b: boolean;
  totalSpendPaise: number;
  visitCount: number;
  lastVisitAt: string | null;
  createdAt: string;
}

/** Mirrors the `keyboard_shortcut` table served by `/api/settings/shortcuts`. */
export interface ShortcutDefinition {
  action: string;
  keyCode: string;
  label: string | null;
  isEnabled: boolean;
}

export interface PrinterProfile {
  mode: string;
  name: string | null;
  paperWidthMm: number;
  charactersPerLine: number;
  autoCut: boolean;
  openDrawer: boolean;
}

/** One batch of a product (`/api/inventory/batches`), soonest expiry first. */
export interface ProductBatch {
  batchId: string;
  batchNumber: string | null;
  expiryDate: string | null;
  availableMilli: number;
  purchasePricePaise: number;
  sellingPricePaise: number;
}

/** One row of the append-only stock ledger (`/api/inventory/movements`). */
export interface StockMovement {
  id: string;
  productId: string;
  type: string;
  quantityMilli: number;
  balanceAfterMilli: number;
  occurredAt: string;
  referenceType: string | null;
  referenceNumber: string | null;
  note: string | null;
  reason: string | null;
  productName: string;
  unit: string;
  batchNumber: string | null;
  userName: string | null;
}

export interface SaleLine {
  id: string;
  productId: string;
  productName: string;
  sku: string;
  unit: string;
  qtyMilli: number;
  ratePaise: number;
  /** Discount applied to this line, in paise of the line gross. */
  discountPaise: number;
  discountBps: number;
  taxablePaise: number;
  taxPaise: number;
  amountPaise: number;
  batchId: string | null;
}